import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCardIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  DocumentTextIcon,
  ServerIcon,
  XMarkIcon,
  UserGroupIcon,
  CpuChipIcon,
  EnvelopeIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
  PhoneIcon,
  SignalIcon
} from '@heroicons/react/24/outline';
import { CreditCardIcon as CreditCardIconSolid } from '@heroicons/react/24/solid';

import axiosInstance from '../api/axiosInstance';
import { billingService } from '../../services/api';
import BreadcrumbNavigation from './BreadcrumbNavigation';
import PaymentMethodModal from '../billing/PaymentMethodModal';
import logger from '../../utils/logger';

// Tellency ERP tier pricing (monthly / annual)
const erpTierPrices = {
  monthly: { starter: 79, basic: 79, professional: 119, power: 119, enterprise: 0, admin: 0 },
  annually: { starter: 66, basic: 66, professional: 99, power: 99, enterprise: 0, admin: 0 }
};

const ModernBilling = ({ showNotification }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [billingData, setBillingData] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethodDetails, setPaymentMethodDetails] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [savingCycle, setSavingCycle] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  // `breakdown.total` is ALREADY the full billing-cycle amount: the backend
  // multiplies the recurring lines by cycleMonths before returning it
  // (billingService.js). Multiplying it by 12 again in the UI showed an annual
  // plan of $100/mo as $14,400/yr instead of $1,200/yr. Derive both figures
  // once, here, so no call site has to guess which one it is holding.
  const cycleMonths = breakdown?.cycleMonths || 1;
  const cycleTotal = breakdown?.total || 0;
  const monthlyEquivalent = breakdown?.recurringMonthlySubtotal ?? (cycleTotal / cycleMonths);

  useEffect(() => {
    fetchBillingData();

    // Check for successful payment session
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      handlePaymentSuccess(sessionId);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const fetchBillingData = async () => {
    try {
      setLoading(true);

      const [businessRes, usersRes, erpUsersRes, paymentMethodData, billingPreviewData] = await Promise.all([
        axiosInstance.get('/api/business').catch(() => ({ data: null })),
        axiosInstance.get('/api/business/users', { params: { limit: 10000, page: 1 } }).catch(() => ({ data: { users: [] } })),
        axiosInstance.get('/api/business/erp-users/assignments').catch(() => ({ data: { assignments: [] } })),
        billingService.getPaymentMethod().catch(() =>
          axiosInstance.get('/api/business/stripe/payment-method-details').then(r => r.data).catch(() =>
            ({ hasPaymentMethod: false })
          )
        ),
        billingService.getBillingPreview().catch(() => null)
      ]);

      // Payment method
      if (paymentMethodData?.hasPaymentMethod || paymentMethodData?.paymentMethod) {
        setPaymentMethodDetails(paymentMethodData.paymentMethod);
      } else {
        setPaymentMethodDetails(null);
      }

      const business = businessRes?.data?.business || {};
      const users = usersRes?.data?.users || usersRes?.users || [];
      const erpUsers = Array.isArray(erpUsersRes.data?.assignments) ? erpUsersRes.data.assignments : [];
      const totalUsers = users.length;

      // Build breakdown from billing preview or fallback
      let billingBreakdown;
      if (billingPreviewData?.billing?.breakdown) {
        const bd = billingPreviewData.billing.breakdown;
        billingBreakdown = {
          emailHosting: {
            users: bd.emailHosting?.users ?? totalUsers,
            pricePerUser: bd.emailHosting?.pricePerUserDollars || 3,
            subtotal: bd.emailHosting?.subtotalDollars ?? 0
          },
          irisPro: {
            users: bd.irisPro?.users || 0,
            pricePerUser: bd.irisPro?.pricePerUserDollars || 29,
            subtotal: bd.irisPro?.subtotalDollars || 0
          },
          irisAutonomous: {
            enabled: bd.irisAutonomous?.enabled || false,
            tier: bd.irisAutonomous?.tier || null,
            subtotal: bd.irisAutonomous?.subtotalDollars || 0
          },
          erpUsers: {
            users: bd.erpUsers?.users || 0,
            tierBreakdown: bd.erpUsers?.tierBreakdown || {},
            subtotal: bd.erpUsers?.subtotalDollars || 0
          },
          aiAgents: {
            count: bd.aiAgents?.count || 0,
            pricePerAgent: bd.aiAgents?.pricePerAgent || 199,
            subtotal: bd.aiAgents?.subtotalDollars || 0
          },
          phoneNumbers: {
            count: bd.phoneNumbers?.count || 0,
            pricePerNumber: bd.phoneNumbers?.pricePerNumber || 2,
            subtotalDollars: bd.phoneNumbers?.subtotalDollars || 0
          },
          voiceUsage: {
            totalCalls: bd.voiceUsage?.totalCalls || 0,
            totalMinutes: bd.voiceUsage?.totalMinutes || 0,
            subtotalDollars: bd.voiceUsage?.subtotalDollars || 0
          },
          // NOTE: totalDollars from the API is the FULL CYCLE amount — the
          // backend already multiplied the recurring lines by cycleMonths
          // (billingService.js). It must not be multiplied by 12 again.
          total: billingPreviewData.billing.totalDollars || 0,
          cycleMonths: bd.cycleMonths || 1,
          recurringMonthlySubtotal: (bd.recurringMonthlySubtotal ?? 0) / 100
        };
      } else {
        // Fallback manual calculation
        const cycle = business?.subscriptionDetails?.billingCycle || 'monthly';
        const platformCost = totalUsers * (cycle === 'annually' ? 2.40 : 3);
        const tierPrices = erpTierPrices[cycle] || erpTierPrices.monthly;
        const erpCost = erpUsers.reduce((sum, u) => sum + (tierPrices[u.tier || u.seatType] || 79), 0);
        billingBreakdown = {
          emailHosting: { users: totalUsers, pricePerUser: 3, subtotal: platformCost },
          irisPro: { users: 0, pricePerUser: 29, subtotal: 0 },
          irisAutonomous: { enabled: false, tier: null, subtotal: 0 },
          erpUsers: { users: erpUsers.length, tierBreakdown: {}, subtotal: erpCost },
          aiAgents: { count: 0, pricePerAgent: 199, subtotal: 0 },
          phoneNumbers: { count: 0, pricePerNumber: 2, subtotalDollars: 0 },
          voiceUsage: { totalCalls: 0, totalMinutes: 0, subtotalDollars: 0 },
          // Match the API branch's contract: `total` is the FULL CYCLE amount.
          // This path computes per-month costs, so annualize it here rather
          // than leaving the two branches with opposite meanings.
          total: (platformCost + erpCost) * (cycle === 'annually' ? 12 : 1),
          cycleMonths: cycle === 'annually' ? 12 : 1,
          recurringMonthlySubtotal: platformCost + erpCost
        };
      }

      setBreakdown(billingBreakdown);

      const isTrialActive = business.workspacePolicy?.tier === 'trial' || business.subscriptionDetails?.status === 'trial';
      const trialEndDate = business.workspacePolicy?.trialEndDate || business.subscriptionDetails?.trialEndDate || business.trial?.endDate;
      const trialDaysRemaining = trialEndDate ? Math.max(0, Math.ceil((new Date(trialEndDate) - new Date()) / (1000 * 60 * 60 * 24))) : 0;

      // Determine next billing date with fallback chain
      let nextBillingDate = business.subscriptionDetails?.nextBillingDate
        || business.billing?.nextBillingDate
        || (isTrialActive ? trialEndDate : null);

      // For active subscriptions with no nextBillingDate, estimate from startDate + billing cycle
      if (!nextBillingDate && !isTrialActive && business.subscriptionDetails?.status === 'active') {
        const startDate = business.subscriptionDetails?.startDate || business.subscriptionDetails?.lastPaymentDate;
        if (startDate) {
          const estimated = new Date(startDate);
          if (business.subscriptionDetails?.billingCycle === 'monthly') {
            estimated.setMonth(estimated.getMonth() + 1);
          } else {
            estimated.setFullYear(estimated.getFullYear() + 1);
          }
          // Only use if the estimated date is in the future, otherwise advance from now
          if (estimated > new Date()) {
            nextBillingDate = estimated;
          } else {
            const fromNow = new Date();
            if (business.subscriptionDetails?.billingCycle === 'monthly') {
              fromNow.setMonth(fromNow.getMonth() + 1);
            } else {
              fromNow.setFullYear(fromNow.getFullYear() + 1);
            }
            nextBillingDate = fromNow;
          }
        }
      }

      setBillingData({
        trial: {
          isActive: isTrialActive,
          endDate: trialEndDate,
          daysRemaining: trialDaysRemaining
        },
        billing: {
          status: business.billing?.status || business.subscriptionDetails?.status || 'pending',
          nextBillingDate: nextBillingDate,
          lastBillingAmount: business.billing?.lastBillingAmount || 0,
          gracePeriodEndsAt: business.billing?.gracePeriodEndsAt
        },
        workspacePolicy: business.workspacePolicy,
        subscriptionDetails: business.subscriptionDetails || {},
        stripeCustomerId: business.stripeCustomerId,
        stripePaymentMethodId: business.stripePaymentMethodId,
        billingPreview: billingPreviewData?.billing || null
      });

    } catch (error) {
      logger.error('Error fetching billing data:', error);
      showNotification?.('Failed to load billing information', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const handlePaymentSuccess = async (sessionId) => {
    try {
      const response = await axiosInstance.post('/api/business/stripe/verify-session', { session_id: sessionId });
      if (response.data.success) {
        showNotification?.('Payment successful! Your subscription has been activated.', 'success');
        await fetchBillingData();
      }
    } catch (error) {
      logger.error('Error verifying payment:', error);
      showNotification?.('Payment completed but verification failed. Contact support if issues persist.', 'warning');
    }
  };

  const ensureStripeCustomer = async () => {
    try {
      await axiosInstance.post('/api/business/stripe/customers', { userEmail: 'admin@business.com' });
      return true;
    } catch (error) {
      if (error.response?.status === 200) return true;
      throw error;
    }
  };

  const handleManageBilling = async () => {
    try {
      await ensureStripeCustomer();
      setShowPaymentModal(true);
    } catch (error) {
      logger.error('Error opening payment modal:', error);
      showNotification?.('Error opening payment form. Please try again.', 'error');
    }
  };

  const handlePaymentMethodAdded = async (paymentMethod) => {
    setPaymentMethodDetails(paymentMethod);
    showNotification?.('Payment method added successfully!', 'success');
    await fetchBillingData();
  };

  const handleUpgradeNow = async () => {
    try {
      if (!paymentMethodDetails) {
        showNotification?.('Please add a payment method before upgrading', 'warning');
        setShowPaymentModal(true);
        return;
      }

      const isAnnualCycle = (billingData?.subscriptionDetails?.billingCycle || 'monthly') === 'annually';
      // Already the full-cycle amount — do not multiply again.
      const chargeAmount = cycleTotal.toFixed(2);
      const confirmed = window.confirm(
        `Are you sure you want to upgrade to a paid plan?\n\n` +
        `Your trial will end immediately and you'll be charged $${chargeAmount}/${isAnnualCycle ? 'year' : 'month'} starting now.`
      );
      if (!confirmed) return;

      setLoading(true);
      const policy = billingData?.workspacePolicy;
      let confirmation = {};
      if (policy?.policyVersion === 2) {
        const { data } = await axiosInstance.get('/api/billing/workspace-upgrade-quote');
        const quote = data.quote;
        if (!window.confirm(`Upgrade ${quote.seats} mailbox seat${quote.seats === 1 ? '' : 's'} to Business? Your saved card will be charged $${(quote.totalCents / 100).toFixed(2)} USD before applicable taxes and account credits for the period ending ${new Date(quote.periodEnd).toLocaleDateString()}. Every active mailbox is billed; the minimum is one seat. Other paid products are not charged again by this upgrade.`)) return;
        confirmation = { confirmWorkspaceBilling: true, quoteToken: data.quoteToken };
      }
      const result = await billingService.upgradeFromTrial(confirmation);

      if (result.success) {
        showNotification?.(
          `Successfully upgraded! Your first bill of $${result.data.monthlyAmount?.toFixed(2)} will be charged on ${formatDate(result.data.nextBillingDate)}`,
          'success'
        );
        await fetchBillingData();
      } else {
        throw new Error(result.message || 'Failed to upgrade');
      }
    } catch (error) {
      logger.error('Error upgrading:', error);
      showNotification?.(error.response?.data?.message || error.message || 'Failed to upgrade.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPlan = async () => {
    try {
      setCancelling(true);
      const result = await billingService.cancelSubscription();
      if (result.success) {
        showNotification?.(result.message || 'Plan cancelled successfully.', 'success');
        setShowCancelModal(false);
        await fetchBillingData();
      } else {
        throw new Error(result.message || 'Failed to cancel');
      }
    } catch (error) {
      logger.error('Error cancelling:', error);
      showNotification?.(error.response?.data?.message || 'Failed to cancel plan. Please try again.', 'error');
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    try {
      if (!paymentMethodDetails) {
        showNotification?.('Please add a payment method before reactivating.', 'warning');
        setShowPaymentModal(true);
        return;
      }

      const confirmed = window.confirm(
        `Reactivate your subscription?\n\nYour plan will be reactivated immediately and you'll be charged $${breakdown?.total?.toFixed(2) || '0.00'} on your next billing date.`
      );
      if (!confirmed) return;

      setReactivating(true);
      const result = await billingService.reactivateSubscription();

      if (result.success) {
        showNotification?.(result.message || 'Subscription reactivated!', 'success');
        await fetchBillingData();
      } else {
        throw new Error(result.message || 'Failed to reactivate');
      }
    } catch (error) {
      logger.error('Error reactivating:', error);
      showNotification?.(error.response?.data?.message || 'Failed to reactivate. Please try again.', 'error');
    } finally {
      setReactivating(false);
    }
  };

  const handleViewInvoices = () => {
    navigate('/invoices');
  };

  const handleChangeBillingCycle = async (newCycle) => {
    const currentCycle = billingData?.subscriptionDetails?.billingCycle || 'monthly';

    // Estimate cost for the TARGET cycle using breakdown details
    // breakdown.total reflects current cycle pricing, so we recalculate for the new cycle
    const targetPrices = erpTierPrices[newCycle] || erpTierPrices.monthly;
    const isTargetAnnual = newCycle === 'annually';

    const emailCost = (breakdown?.emailHosting?.users || 0) * (isTargetAnnual ? 2.40 : 3);
    const irisProCost = (breakdown?.irisPro?.users || 0) * (isTargetAnnual ? 23 : 29);
    const autonomousCost = breakdown?.irisAutonomous?.subtotal || 0;
    const agentCost = (breakdown?.aiAgents?.count || 0) * (isTargetAnnual ? 166 : 199);

    // Recalculate ERP cost for target cycle from tier breakdown
    let erpCost = 0;
    if (breakdown?.erpUsers?.tierBreakdown) {
      for (const [tier, count] of Object.entries(breakdown.erpUsers.tierBreakdown)) {
        erpCost += (targetPrices[tier] || (isTargetAnnual ? 66 : 79)) * count;
      }
    } else {
      erpCost = breakdown?.erpUsers?.subtotal || 0;
    }

    const estimatedMonthlyTotal = emailCost + irisProCost + autonomousCost + agentCost + erpCost;

    // Confirm with user before switching — pro-rated charge will apply
    const cycleLabel = isTargetAnnual ? 'annual' : 'monthly';
    const displayTotal = isTargetAnnual ? (estimatedMonthlyTotal * 12).toFixed(2) : estimatedMonthlyTotal.toFixed(2);
    const confirmed = window.confirm(
      `Switch to ${cycleLabel} billing?\n\n` +
      `New ${cycleLabel} cost: $${displayTotal}/${isTargetAnnual ? 'yr' : 'mo'}` +
      (isTargetAnnual ? ` ($${estimatedMonthlyTotal.toFixed(2)}/mo)` : '') +
      `\nA pro-rated charge will be applied for the remaining period. Your card will be charged the difference.`
    );
    if (!confirmed) return;

    try {
      setSavingCycle(true);
      const response = await axiosInstance.put('/api/business/update-plan', {
        subscriptionDetails: {
          plan: billingData?.subscriptionDetails?.plan || 'email-hosting',
          billingCycle: newCycle
        }
      });
      if (response.data.success) {
        const msg = response.data.proration?.invoiceNumber
          ? `Billing cycle changed to ${cycleLabel}. Charged $${response.data.proration.netAmountDollars?.toFixed(2)} (Invoice: ${response.data.proration.invoiceNumber})`
          : `Billing cycle changed to ${cycleLabel}`;
        showNotification?.(msg, 'success');
        await fetchBillingData();
      } else {
        throw new Error(response.data.message || 'Failed to update');
      }
    } catch (error) {
      logger.error('Error changing billing cycle:', error);
      const errorData = error.response?.data;
      const message = errorData?.message || 'Failed to change billing cycle. Please try again.';
      showNotification?.(message, 'error');
      // If payment method needs updating, prompt to open payment modal
      if (errorData?.requiresPaymentUpdate) {
        setTimeout(() => setShowPaymentModal(true), 1500);
      }
    } finally {
      setSavingCycle(false);
    }
  };

  if (loading && !billingData) {
    return (
      <div className="h-full flex flex-col bg-gray-50 dark:bg-[#111111] overflow-hidden">
        <div className="flex items-center justify-center flex-1">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading billing information...</p>
          </div>
        </div>
      </div>
    );
  }

  const billingStatus = billingData?.billing?.status || 'pending';
  const isTrialActive = billingData?.trial?.isActive;

  const statusConfig = {
    trial: { label: 'Free Trial', color: 'green', bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800', text: 'text-green-700 dark:text-green-300' },
    active: { label: 'Active', color: 'green', bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800', text: 'text-green-700 dark:text-green-300' },
    grace_period: { label: 'Grace Period', color: 'yellow', bg: 'bg-yellow-50 dark:bg-yellow-900/20', border: 'border-yellow-200 dark:border-yellow-800', text: 'text-yellow-700 dark:text-yellow-300' },
    past_due: { label: 'Past Due', color: 'red', bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800', text: 'text-red-700 dark:text-red-300' },
    suspended: { label: 'Suspended', color: 'red', bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800', text: 'text-red-700 dark:text-red-300' },
    expired: { label: 'Expired', color: 'red', bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800', text: 'text-red-700 dark:text-red-300' },
    cancelled: { label: 'Cancelled', color: 'gray', bg: 'bg-gray-50 dark:bg-gray-900/20', border: 'border-gray-300 dark:border-gray-700', text: 'text-gray-600 dark:text-gray-400' },
    pending: { label: 'Pending Setup', color: 'yellow', bg: 'bg-yellow-50 dark:bg-yellow-900/20', border: 'border-yellow-200 dark:border-yellow-800', text: 'text-yellow-700 dark:text-yellow-300' }
  };

  const currentStatus = isTrialActive ? statusConfig.trial : (statusConfig[billingStatus] || statusConfig.pending);

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-[#111111] overflow-hidden">
      {/* Header */}
      <div className="bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800 px-6 py-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCardIconSolid className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <h1 className="text-sm font-semibold text-gray-900 dark:text-white">Billing & Subscriptions</h1>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {isTrialActive
                ? `Trial · ${billingData.trial.daysRemaining}d left`
                : billingStatus === 'cancelled'
                  ? 'Cancelled'
                  : `${(billingData?.subscriptionDetails?.billingCycle || 'monthly') === 'annually' ? 'Annual' : 'Monthly'} · $${cycleTotal.toFixed(2)}/${(billingData?.subscriptionDetails?.billingCycle || 'monthly') === 'annually' ? 'yr' : 'mo'}`
              }
            </span>
          </div>
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${currentStatus.bg} ${currentStatus.text} border ${currentStatus.border}`}>
            {currentStatus.label}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-auto p-4">
        <div className="max-w-5xl mx-auto space-y-4">

          {billingData?.workspacePolicy?.policyVersion === 2 && <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
            <h2 className="font-semibold">Workspace {billingData.workspacePolicy.tier === 'free' ? 'Starter' : billingData.workspacePolicy.tier === 'trial' ? 'Business trial' : 'Business'}</h2>
            <p className="text-sm mt-2">{billingData.workspacePolicy.tier === 'free' ? 'One free mailbox with 5GB storage, plus 10GB shared Drive.' : '50GB per mailbox and 50GB shared Drive. Paid Business bills every active mailbox at $3/month, with a one-seat minimum.'}</p>
            {billingData.workspacePolicy.tier !== 'paid' && <p className="text-sm text-gray-500 mt-2">No automatic charges when the trial ends. Your workspace returns to the free allowance unless you confirm an upgrade. Existing data is retained.</p>}
            <div className="flex flex-wrap gap-3 mt-4">
              {billingData.workspacePolicy.canStartTrial && <button type="button" className="px-4 py-2 border rounded-lg" onClick={async()=>{try{await axiosInstance.post('/api/business/workspace-trial');showNotification?.('Your 14-day Business trial has started.','success');fetchBillingData();}catch(error){showNotification?.(error.response?.data?.message || 'Could not start trial.','error');}}}>Start 14-day trial</button>}
              {billingData.workspacePolicy.tier !== 'paid' && <button type="button" className="px-4 py-2 bg-blue-600 text-white rounded-lg" onClick={handleUpgradeNow}>Upgrade to Business</button>}
            </div>
          </section>}
          {/* Status Banner */}
          {isTrialActive && (
            <div className={`rounded-lg border ${statusConfig.trial.border} ${statusConfig.trial.bg} px-3 py-2`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <SparklesIcon className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <p className="text-xs text-green-700 dark:text-green-300">
                    Trial ends {formatDate(billingData.trial.endDate)} — {billingData.trial.daysRemaining}d remaining
                    {!paymentMethodDetails && ' · Add payment to avoid interruption'}
                  </p>
                </div>
                {!paymentMethodDetails ? (
                  <button
                    onClick={() => setShowPaymentModal(true)}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer flex-shrink-0 ml-3"
                  >
                    Add Payment
                  </button>
                ) : (
                  <button
                    onClick={handleUpgradeNow}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer flex-shrink-0 ml-3"
                  >
                    Upgrade Now
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Grace Period / Past Due / Suspended / Cancelled Banner */}
          {!isTrialActive && ['grace_period', 'past_due', 'suspended', 'expired', 'cancelled'].includes(billingStatus) && (
            <div className={`rounded-xl border ${currentStatus.border} ${currentStatus.bg} p-4`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ExclamationTriangleIcon className={`w-6 h-6 ${billingStatus === 'grace_period' ? 'text-yellow-600' : billingStatus === 'cancelled' ? 'text-gray-500' : 'text-red-600'}`} />
                  <div>
                    <h3 className={`text-sm font-semibold ${currentStatus.text}`}>
                      {billingStatus === 'grace_period' && 'Payment Failed — Grace Period'}
                      {billingStatus === 'past_due' && 'Payment Past Due'}
                      {billingStatus === 'suspended' && 'Account Suspended'}
                      {billingStatus === 'expired' && 'Subscription Expired'}
                      {billingStatus === 'cancelled' && 'Plan Cancelled'}
                    </h3>
                    <p className={`text-xs ${currentStatus.text} opacity-80`}>
                      {billingStatus === 'grace_period' && `Please update your payment method before ${formatDate(billingData.billing.gracePeriodEndsAt)} to avoid service interruption.`}
                      {billingStatus === 'past_due' && 'Your payment method has been declined. Please update it to restore full access.'}
                      {billingStatus === 'suspended' && 'Your account has been suspended due to non-payment. Please update your payment method to restore access.'}
                      {billingStatus === 'expired' && 'Your subscription has expired. Please add a payment method and reactivate your plan.'}
                      {billingStatus === 'cancelled' && (
                        billingData?.subscriptionDetails?.scheduledCancellation?.effectiveDate
                          ? `Your plan has been cancelled. Access continues until ${formatDate(billingData.subscriptionDetails.scheduledCancellation.effectiveDate)}.`
                          : 'Your plan has been cancelled.'
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {['cancelled', 'expired', 'suspended'].includes(billingStatus) && (
                    <button
                      onClick={handleReactivate}
                      disabled={reactivating}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
                    >
                      <ArrowPathIcon className={`w-3.5 h-3.5 ${reactivating ? 'animate-spin' : ''}`} />
                      {reactivating ? 'Reactivating...' : 'Reactivate Plan'}
                    </button>
                  )}
                  {['grace_period', 'past_due', 'suspended'].includes(billingStatus) && (
                    <button
                      onClick={() => setShowPaymentModal(true)}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer"
                    >
                      Update Payment
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Billing Cycle & Next Payment Info */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-gray-800 p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Billing Cycle</p>
              <div className="flex items-center justify-between">
                {(() => {
                  const currentCycle = billingData?.billingPreview?.billingCycle || billingData?.subscriptionDetails?.billingCycle || 'monthly';
                  const isMonthly = currentCycle === 'monthly';
                  return (
                    <div className="flex items-center gap-2 mt-1">
                      <div className="inline-flex items-center bg-gray-100 dark:bg-[#222222] rounded-lg p-0.5">
                        <button
                          onClick={() => !isMonthly && handleChangeBillingCycle('monthly')}
                          disabled={savingCycle || isMonthly}
                          className={`px-3 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer ${
                            isMonthly
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                          }`}
                        >
                          Monthly
                        </button>
                        <button
                          onClick={() => isMonthly && handleChangeBillingCycle('annually')}
                          disabled={savingCycle || !isMonthly}
                          className={`px-3 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer ${
                            !isMonthly
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                          }`}
                        >
                          Annual
                        </button>
                      </div>
                      {!isMonthly && (
                        <span className="text-[10px] text-green-600 dark:text-green-400 font-medium">Save ~17%</span>
                      )}
                      {savingCycle && (
                        <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-blue-600"></div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
            {(() => {
              // When an account is overdue, nextBillingDate stays at the original
              // due date (it only advances on a successful payment). Showing a
              // past date under "Next Payment" is misleading — relabel it as the
              // overdue/due date instead.
              const nbd = billingData?.billing?.nextBillingDate;
              const isOverdueStatus = ['past_due', 'grace_period', 'suspended'].includes(billingStatus);
              const isPastDate = nbd && new Date(nbd) < new Date();
              const showOverdue = !isTrialActive && isOverdueStatus && isPastDate;

              const label = isTrialActive
                ? 'Trial Ends'
                : showOverdue
                  ? 'Payment Overdue'
                  : 'Next Payment';

              return (
                <div className="bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-gray-800 p-4">
                  <p className={`text-xs mb-1 ${showOverdue ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                    {label}
                  </p>
                  <p className={`text-lg font-bold ${showOverdue ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                    {nbd ? formatDate(nbd) : 'N/A'}
                  </p>
                  {showOverdue && (
                    <p className="text-xs text-red-500 dark:text-red-400/80 mt-0.5">Payment past due</p>
                  )}
                </div>
              );
            })()}
            <div className="bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-gray-800 p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Amount Due</p>
              {(() => {
                const isAnnualCycle = (billingData?.subscriptionDetails?.billingCycle || 'monthly') === 'annually';
                return (
                  <>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      ${isTrialActive ? '0.00' : cycleTotal.toFixed(2)}
                    </p>
                    {!isTrialActive && isAnnualCycle && cycleTotal > 0 && (
                      <p className="text-xs text-green-600 dark:text-green-400">${monthlyEquivalent.toFixed(2)}/mo billed annually</p>
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          {/* Billing Summary Card */}
          <div className="bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-gray-800">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <CurrencyDollarIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                    {(billingData?.subscriptionDetails?.billingCycle || 'monthly') === 'annually' ? 'Annual' : 'Monthly'} Cost Breakdown
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {isTrialActive ? 'Estimated cost after trial ends' : `Current ${(billingData?.subscriptionDetails?.billingCycle || 'monthly') === 'annually' ? 'annual' : 'monthly'} charges`}
                  </p>
                </div>
              </div>
              <div className="text-right">
                {(() => {
                  const isAnnualCycle = (billingData?.subscriptionDetails?.billingCycle || 'monthly') === 'annually';
                  return (
                    <>
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        ${isTrialActive ? '0.00' : cycleTotal.toFixed(2)}
                        <span className="text-sm font-normal text-gray-400">/{isAnnualCycle ? 'yr' : 'mo'}</span>
                      </div>
                      {isTrialActive && cycleTotal > 0 && (
                        <p className="text-xs text-gray-400">After trial: ${cycleTotal.toFixed(2)}{isAnnualCycle ? '/yr' : '/mo'}</p>
                      )}
                      {!isTrialActive && isAnnualCycle && cycleTotal > 0 && (
                        <p className="text-xs text-gray-400">${monthlyEquivalent.toFixed(2)}/mo equivalent</p>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="p-5">
              <div className="space-y-3">
                {/* Email Hosting */}
                <LineItem
                  icon={<EnvelopeIcon className="w-4 h-4" />}
                  iconBg="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                  label="Platform (Email Hosting)"
                  detail={`${breakdown?.emailHosting?.users || 0} users x $${breakdown?.emailHosting?.pricePerUser || 3}/user`}
                  amount={breakdown?.emailHosting?.subtotal || 0}
                />

                {/* ERP Users */}
                {(breakdown?.erpUsers?.users > 0 || breakdown?.erpUsers?.subtotal > 0) && (
                  <LineItem
                    icon={<UserGroupIcon className="w-4 h-4" />}
                    iconBg="bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400"
                    label="ERP User Seats"
                    detail={formatErpDetail(breakdown?.erpUsers, billingData?.subscriptionDetails?.billingCycle || 'monthly')}
                    amount={breakdown?.erpUsers?.subtotal || 0}
                  />
                )}

                {/* AI Agents */}
                {(breakdown?.aiAgents?.count > 0 || breakdown?.aiAgents?.subtotal > 0) && (
                  <LineItem
                    icon={<CpuChipIcon className="w-4 h-4" />}
                    iconBg="bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400"
                    label="AI Agents"
                    detail={`${breakdown?.aiAgents?.count || 0} agent${(breakdown?.aiAgents?.count || 0) !== 1 ? 's' : ''} x $${breakdown?.aiAgents?.pricePerAgent || 199}/agent`}
                    amount={breakdown?.aiAgents?.subtotal || 0}
                  />
                )}

                {/* Iris Pro */}
                {(breakdown?.irisPro?.users > 0 || breakdown?.irisPro?.subtotal > 0) && (
                  <LineItem
                    icon={<SparklesIcon className="w-4 h-4" />}
                    iconBg="bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400"
                    label="Iris Pro"
                    detail={`${breakdown?.irisPro?.users || 0} users x $${breakdown?.irisPro?.pricePerUser || 29}/user`}
                    amount={breakdown?.irisPro?.subtotal || 0}
                  />
                )}

                {/* Phone Numbers (DIDs) */}
                {(breakdown?.phoneNumbers?.count > 0 || breakdown?.phoneNumbers?.subtotal > 0) && (
                  <LineItem
                    icon={<PhoneIcon className="w-4 h-4" />}
                    iconBg="bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400"
                    label="Phone Numbers"
                    detail={`${breakdown?.phoneNumbers?.count || 0} DID${(breakdown?.phoneNumbers?.count || 0) !== 1 ? 's' : ''} x $${breakdown?.phoneNumbers?.pricePerNumber || 2}/number`}
                    amount={breakdown?.phoneNumbers?.subtotalDollars || 0}
                  />
                )}

                {/* Voice Usage */}
                {(breakdown?.voiceUsage?.totalCalls > 0 || breakdown?.voiceUsage?.subtotal > 0) && (
                  <LineItem
                    icon={<SignalIcon className="w-4 h-4" />}
                    iconBg="bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400"
                    label="Voice Usage"
                    detail={`${breakdown?.voiceUsage?.totalCalls || 0} calls, ${breakdown?.voiceUsage?.totalMinutes || 0} min`}
                    amount={breakdown?.voiceUsage?.subtotalDollars || 0}
                  />
                )}

                {/* Iris Autonomous */}
                {breakdown?.irisAutonomous?.enabled && (
                  <LineItem
                    icon={<ServerIcon className="w-4 h-4" />}
                    iconBg="bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400"
                    label={`Iris Autonomous (${breakdown.irisAutonomous.tier || 'N/A'})`}
                    detail="Organization-wide license"
                    amount={breakdown?.irisAutonomous?.subtotal || 0}
                  />
                )}

                {/* Total */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3 flex items-center justify-between">
                  <div>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {(billingData?.subscriptionDetails?.billingCycle || 'monthly') === 'annually' ? 'Total Annual Cost' : 'Total Monthly Cost'}
                    </span>
                    {(billingData?.subscriptionDetails?.billingCycle || 'monthly') === 'annually' && (
                      <p className="text-xs text-gray-400">${monthlyEquivalent.toFixed(2)}/mo x {breakdown?.cycleMonths || 12} months</p>
                    )}
                  </div>
                  <span className="text-lg font-bold text-gray-900 dark:text-white">
                    ${cycleTotal.toFixed(2)}
                  </span>
                </div>

                {/* No active services */}
                {!breakdown?.emailHosting?.users && !breakdown?.erpUsers?.users && !breakdown?.aiAgents?.count && !breakdown?.irisPro?.users && (
                  <p className="text-xs text-gray-400 text-center py-2">No active services. Visit License Management to configure your services.</p>
                )}
              </div>
            </div>
          </div>

          {/* Payment Method Card */}
          <div className="bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-gray-800">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <CreditCardIconSolid className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white">Payment Method</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {paymentMethodDetails ? 'Your payment method is configured' : 'Add a payment method to continue after trial'}
                  </p>
                </div>
              </div>
              {paymentMethodDetails && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800">Active</span>
              )}
            </div>

            <div className="p-5">
              {paymentMethodDetails ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#222222] rounded-lg">
                    <div className="flex items-center gap-3">
                      <CreditCardIcon className="w-6 h-6 text-gray-500 dark:text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {(paymentMethodDetails.brand || 'Card')?.charAt(0).toUpperCase() + (paymentMethodDetails.brand || 'card')?.slice(1)} •••• {paymentMethodDetails.last4}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Expires {paymentMethodDetails.exp_month}/{paymentMethodDetails.exp_year}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleManageBilling}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 font-medium cursor-pointer"
                    >
                      Update
                    </button>
                  </div>
                  <p className="text-xs text-gray-400">
                    {isTrialActive
                      ? 'You will be charged automatically when your trial ends unless you cancel.'
                      : 'This card will be charged on your next billing date.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <div className="flex items-start gap-2.5">
                      <ExclamationCircleIcon className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="text-xs font-medium text-yellow-800 dark:text-yellow-200">Payment method required</h4>
                        <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-0.5">
                          Add a payment method to ensure uninterrupted service when your free trial ends.
                        </p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleManageBilling}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors cursor-pointer"
                  >
                    <CreditCardIcon className="w-3.5 h-3.5" />
                    Add Payment Method
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button
              onClick={handleViewInvoices}
              className="bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-left hover:border-blue-300 dark:hover:border-blue-700 transition-colors cursor-pointer group"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#222222] flex items-center justify-center group-hover:bg-blue-50 dark:group-hover:bg-blue-900/20 transition-colors">
                  <DocumentTextIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">View Invoices</p>
                  <p className="text-xs text-gray-400">Billing history & receipts</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate('/license-management')}
              className="bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-left hover:border-purple-300 dark:hover:border-purple-700 transition-colors cursor-pointer group"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#222222] flex items-center justify-center group-hover:bg-purple-50 dark:group-hover:bg-purple-900/20 transition-colors">
                  <ServerIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Manage Licenses</p>
                  <p className="text-xs text-gray-400">Modules, users & agents</p>
                </div>
              </div>
            </button>

            {['cancelled', 'expired', 'suspended'].includes(billingStatus) ? (
              <button
                onClick={handleReactivate}
                disabled={reactivating}
                className="bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-left hover:border-green-300 dark:hover:border-green-700 transition-colors cursor-pointer group disabled:opacity-50"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#222222] flex items-center justify-center group-hover:bg-green-50 dark:group-hover:bg-green-900/20 transition-colors">
                    <ArrowPathIcon className={`w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-green-600 dark:group-hover:text-green-400 ${reactivating ? 'animate-spin' : ''}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {reactivating ? 'Reactivating...' : 'Reactivate Plan'}
                    </p>
                    <p className="text-xs text-gray-400">Resume your subscription</p>
                  </div>
                </div>
              </button>
            ) : (isTrialActive || ['active', 'trial', 'past_due'].includes(billingStatus)) && (
              <button
                onClick={() => setShowCancelModal(true)}
                className="bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-left hover:border-red-300 dark:hover:border-red-700 transition-colors cursor-pointer group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-[#222222] flex items-center justify-center group-hover:bg-red-50 dark:group-hover:bg-red-900/20 transition-colors">
                    <XMarkIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-red-600 dark:group-hover:text-red-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Cancel Plan</p>
                    <p className="text-xs text-gray-400">End your subscription</p>
                  </div>
                </div>
              </button>
            )}
          </div>

        </div>
      </div>

      {/* Cancel Plan Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-xl max-w-md w-full border border-gray-200 dark:border-gray-700">
            <div className="p-5 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Cancel Plan</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">This action cannot be undone</p>
                </div>
              </div>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                {isTrialActive
                  ? 'Are you sure you want to cancel your trial? Your access will end immediately and all users will lose access to the platform.'
                  : `Are you sure you want to cancel your subscription? Your access will continue until the end of your current billing period${billingData?.billing?.nextBillingDate ? ` (${formatDate(billingData.billing.nextBillingDate)})` : ''}. After that, all users will lose access to the platform.`
                }
              </p>
              <div className="flex items-center gap-3 justify-end">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
                >
                  Keep Plan
                </button>
                <button
                  onClick={handleCancelPlan}
                  disabled={cancelling}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 cursor-pointer"
                >
                  {cancelling ? 'Cancelling...' : 'Yes, Cancel Plan'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Method Modal */}
      <PaymentMethodModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={handlePaymentMethodAdded}
        showNotification={showNotification}
        currentPlan={billingData?.subscriptionDetails?.plan || 'business'}
      />
    </div>
  );
};

// Line item component for billing breakdown
const LineItem = ({ icon, iconBg, label, detail, amount }) => (
  <div className="flex items-center justify-between py-2">
    <div className="flex items-center gap-3">
      <div className={`w-7 h-7 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
      <div>
        <p className="text-sm font-medium text-gray-900 dark:text-white">{label}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{detail}</p>
      </div>
    </div>
    <span className="text-sm font-semibold text-gray-900 dark:text-white">${amount.toFixed(2)}</span>
  </div>
);

// Format ERP users detail string
const formatErpDetail = (erpUsers, billingCycle = 'monthly') => {
  if (!erpUsers || !erpUsers.users) return '0 seats';
  const tb = erpUsers.tierBreakdown || {};
  const parts = [];
  const isAnnual = billingCycle === 'annually';
  const tierNames = {
    starter: `Starter ($${isAnnual ? '66' : '79'})`, basic: `Starter ($${isAnnual ? '66' : '79'})`,
    professional: `Pro ($${isAnnual ? '99' : '119'})`, power: `Pro ($${isAnnual ? '99' : '119'})`,
    enterprise: 'Enterprise', admin: 'Enterprise'
  };

  for (const [tier, count] of Object.entries(tb)) {
    parts.push(`${count} ${tierNames[tier] || tier}`);
  }

  return parts.length > 0 ? parts.join(', ') : `${erpUsers.users} seat${erpUsers.users !== 1 ? 's' : ''}`;
};

export default ModernBilling;
